import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { User, Users, Kanban, Save, Shield, UserPlus, Eye, EyeOff, Lock, Database, Tag, Plus, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const ROLE_CONFIG = {
  admin: { label: 'Admin', color: 'bg-[#EF4444]/10 text-[#EF4444]' },
  manager: { label: 'Manager', color: 'bg-[#FFAB00]/10 text-[#B07800]' },
  commercial: { label: 'Commercial', color: 'bg-[#3B82F6]/10 text-[#3B82F6]' },
};

export default function Settings() {
  const { user, updateUser } = useAuth();
  const [team, setTeam] = useState([]);
  const [stages, setStages] = useState([]);
  const [profileForm, setProfileForm] = useState({ name: '', position: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', role: 'commercial' });
  const [showPass, setShowPass] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPwFields, setShowPwFields] = useState({ current: false, newpw: false, confirm: false });
  const [savingPw, setSavingPw] = useState(false);
  const [offers, setOffers] = useState([]);
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [offerForm, setOfferForm] = useState({ name: '', description: '', deposit_type: null, deposit_value: 0, initial_amount: 0, initial_installments: 1, has_recurring: false, recurring_amount: 0, recurring_frequency: 'monthly', recurring_count: 12 });
  const [savingOffer, setSavingOffer] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name || '', position: user.position || '', phone: user.phone || '' });
    }
    Promise.all([api.get('/team'), api.get('/pipeline/stages'), api.get('/offers')])
      .then(([t, s, o]) => { setTeam(t.data); setStages(s.data); setOffers(o.data); })
      .catch(() => toast.error('Erreur chargement'));
  }, [user]);

  const handleSaveProfile = async () => {
    if (!profileForm.name) { toast.error('Le nom est requis'); return; }
    setSaving(true);
    try {
      const res = await api.put('/auth/profile', profileForm);
      updateUser(res.data);
      toast.success('Profil mis à jour');
    } catch { toast.error('Erreur lors de la mise à jour'); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password || !pwForm.confirm_password) {
      toast.error('Tous les champs sont requis'); return;
    }
    if (pwForm.new_password.length < 6) {
      toast.error('Minimum 6 caractères'); return;
    }
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas'); return;
    }
    setSavingPw(true);
    try {
      await api.post('/auth/change-password', {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password
      });
      toast.success('Mot de passe modifié');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSavingPw(false); }
  };

  const handleBackupDB = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_URL}/api/admin/backup`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erreur');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm_backup_${new Date().toISOString().substring(0,10)}.json`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success('Backup exporté avec succès');
    } catch { toast.error('Erreur lors du backup'); }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await api.put(`/team/${userId}/role`, { role });
      setTeam(prev => prev.map(u => u.id === userId ? { ...u, role } : u));
      toast.success('Rôle mis à jour');
    } catch { toast.error('Accès refusé ou erreur'); }
  };

  const handleCreateUser = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error('Tous les champs sont requis');
      return;
    }
    if (createForm.password.length < 6) {
      toast.error('Mot de passe minimum 6 caractères');
      return;
    }
    setCreatingUser(true);
    try {
      const res = await api.post('/team/create-user', createForm);
      setTeam(prev => [...prev, res.data]);
      setShowCreateUser(false);
      setCreateForm({ name: '', email: '', password: '', role: 'commercial' });
      toast.success(`Compte créé pour ${res.data.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreatingUser(false);
    }
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  const avatarColors = ['bg-[#6366F1]/20 text-[#6366F1]', 'bg-[#3B82F6]/20 text-[#3B82F6]', 'bg-[#1B99E5]/20 text-[#1B99E5]', 'bg-[#F97316]/20 text-[#F97316]'];
  const getAvatarColor = (id) => avatarColors[(id?.charCodeAt(0) || 0) % avatarColors.length];

  const openNewOffer = () => {
    setEditingOffer(null);
    setOfferForm({ name: '', description: '', deposit_type: null, deposit_value: 0, initial_amount: 0, initial_installments: 1, has_recurring: false, recurring_amount: 0, recurring_frequency: 'monthly', recurring_count: 12 });
    setShowOfferDialog(true);
  };

  const openEditOffer = (offer) => {
    setEditingOffer(offer);
    setOfferForm({ name: offer.name || '', description: offer.description || '', deposit_type: offer.deposit_type || null, deposit_value: offer.deposit_value || 0, initial_amount: offer.initial_amount || 0, initial_installments: offer.initial_installments || 1, has_recurring: offer.has_recurring || false, recurring_amount: offer.recurring_amount || 0, recurring_frequency: offer.recurring_frequency || 'monthly', recurring_count: offer.recurring_count || 12 });
    setShowOfferDialog(true);
  };

  const handleSaveOffer = async () => {
    if (!offerForm.name) { toast.error('Le nom est requis'); return; }
    setSavingOffer(true);
    try {
      if (editingOffer) {
        const res = await api.put(`/offers/${editingOffer.id}`, offerForm);
        setOffers(prev => prev.map(o => o.id === editingOffer.id ? res.data : o));
        toast.success('Offre mise à jour');
      } else {
        const res = await api.post('/offers', offerForm);
        setOffers(prev => [...prev, res.data]);
        toast.success('Offre créée');
      }
      setShowOfferDialog(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSavingOffer(false); }
  };

  const handleDeleteOffer = async (oid) => {
    try {
      await api.delete(`/offers/${oid}`);
      setOffers(prev => prev.filter(o => o.id !== oid));
      toast.success('Offre supprimée');
    } catch { toast.error('Erreur'); }
  };

  const FREQ_LABELS = { monthly: 'mensuel', quarterly: 'trimestriel', annual: 'annuel' };

  return (
    <div className="p-6">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Paramètres</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Gérez votre profil et votre équipe</p>
      </div>

      <div className="max-w-3xl">
        <Tabs defaultValue="profile">
          <TabsList className="mb-5 bg-white dark:bg-[#1A1D23] border border-gray-200 dark:border-gray-700 p-1">
            <TabsTrigger value="profile" className="text-sm" data-testid="tab-profile">
              <User size={14} className="mr-1.5" /> Profil
            </TabsTrigger>
            <TabsTrigger value="team" className="text-sm" data-testid="tab-team">
              <Users size={14} className="mr-1.5" /> Équipe
            </TabsTrigger>
            <TabsTrigger value="pipeline" className="text-sm" data-testid="tab-pipeline">
              <Kanban size={14} className="mr-1.5" /> Pipeline
            </TabsTrigger>
            <TabsTrigger value="offers" className="text-sm" data-testid="tab-offers">
              <Tag size={14} className="mr-1.5" /> Modèles d'offres
            </TabsTrigger>
          </TabsList>

          {/* Profile tab */}
          <TabsContent value="profile">
            <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-6">
              {/* Avatar */}
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100 dark:border-gray-800">
                <Avatar className="h-16 w-16">
                  <AvatarFallback className="bg-[#1B99E5]/20 text-[#1B99E5] text-xl font-bold">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white text-base">{user?.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
                  <span className={`mt-1 inline-flex text-xs font-medium px-2 py-0.5 rounded-full ${ROLE_CONFIG[user?.role]?.color || 'bg-gray-100 text-gray-600'}`}>
                    {ROLE_CONFIG[user?.role]?.label || user?.role}
                  </span>
                </div>
              </div>

              {/* Form */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">Nom complet *</Label>
                  <Input
                    value={profileForm.name}
                    onChange={e => setProfileForm({ ...profileForm, name: e.target.value })}
                    className="max-w-sm"
                    data-testid="profile-name-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">Email</Label>
                  <Input value={user?.email || ''} disabled className="max-w-sm bg-gray-50 dark:bg-gray-800 text-gray-400" />
                  <p className="text-xs text-gray-400 mt-1">L'email ne peut pas être modifié</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">Poste / Titre</Label>
                  <Input
                    placeholder="Ex: Commercial Senior"
                    value={profileForm.position}
                    onChange={e => setProfileForm({ ...profileForm, position: e.target.value })}
                    className="max-w-sm"
                    data-testid="profile-position-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">Téléphone</Label>
                  <Input
                    placeholder="+33 6 00 00 00 00"
                    value={profileForm.phone}
                    onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
                    className="max-w-sm"
                    data-testid="profile-phone-input"
                  />
                </div>
                <div className="pt-2">
                  <Button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white h-9 px-5 active:scale-95"
                    style={{ transition: 'background-color 150ms ease, transform 100ms ease' }}
                    data-testid="save-profile-btn"
                  >
                    <Save size={14} className="mr-1.5" />
                    {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                </div>

                {/* Password change */}
                <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-800">
                  <div className="flex items-center gap-2 mb-4">
                    <Lock size={15} className="text-gray-400" />
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Modifier le mot de passe</h4>
                  </div>
                  <div className="space-y-3 max-w-sm">
                    <div>
                      <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">Mot de passe actuel</Label>
                      <div className="relative">
                        <Input
                          type={showPwFields.current ? 'text' : 'password'}
                          value={pwForm.current_password}
                          onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })}
                          placeholder="••••••••"
                          className="pr-10"
                          data-testid="current-password-input"
                        />
                        <button type="button" onClick={() => setShowPwFields(p => ({ ...p, current: !p.current }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showPwFields.current ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">Nouveau mot de passe</Label>
                      <div className="relative">
                        <Input
                          type={showPwFields.newpw ? 'text' : 'password'}
                          value={pwForm.new_password}
                          onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })}
                          placeholder="Min. 6 caractères"
                          className="pr-10"
                          data-testid="new-password-input"
                        />
                        <button type="button" onClick={() => setShowPwFields(p => ({ ...p, newpw: !p.newpw }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showPwFields.newpw ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">Confirmer le nouveau mot de passe</Label>
                      <div className="relative">
                        <Input
                          type={showPwFields.confirm ? 'text' : 'password'}
                          value={pwForm.confirm_password}
                          onChange={e => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                          placeholder="••••••••"
                          className="pr-10"
                          data-testid="confirm-password-input"
                        />
                        <button type="button" onClick={() => setShowPwFields(p => ({ ...p, confirm: !p.confirm }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showPwFields.confirm ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>
                    <Button
                      onClick={handleChangePassword}
                      disabled={savingPw}
                      variant="outline"
                      className="h-9 px-5 border-[#1B99E5] text-[#1B99E5] hover:bg-[#1B99E5]/10 active:scale-95"
                      style={{ transition: 'all 150ms ease' }}
                      data-testid="change-password-btn"
                    >
                      <Lock size={13} className="mr-1.5" />
                      {savingPw ? 'Modification...' : 'Changer le mot de passe'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Team tab */}
          <TabsContent value="team">
            <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
              <div className="p-5 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Membres de l'équipe</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{team.length} membre{team.length !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {user?.role === 'admin' && (
                      <div className="flex items-center gap-1.5 text-xs text-[#1B99E5] bg-[#1B99E5]/10 px-2.5 py-1 rounded-full">
                        <Shield size={11} /> Mode admin
                      </div>
                    )}
                    {user?.role === 'admin' && (
                      <Button onClick={() => setShowCreateUser(true)} size="sm" className="h-8 bg-[#1B99E5] hover:bg-[#0f86cc] text-white text-xs" data-testid="create-user-btn">
                        <UserPlus size={13} className="mr-1" /> Inviter
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Utilisateur</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Rôle</th>
                  </tr>
                </thead>
                <tbody>
                  {team.map(member => (
                    <tr key={member.id} className="border-b border-gray-100 dark:border-gray-800 last:border-0 h-14" data-testid={`team-member-${member.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8 flex-shrink-0">
                            <AvatarFallback className={`text-xs font-bold ${getAvatarColor(member.id)}`}>
                              {getInitials(member.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {member.name}
                              {member.id === user?.id && <span className="ml-1.5 text-xs text-gray-400">(vous)</span>}
                            </p>
                            {member.position && <p className="text-xs text-gray-400">{member.position}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="text-sm text-gray-500 dark:text-gray-400">{member.email}</span>
                      </td>
                      <td className="px-4 py-3">
                        {user?.role === 'admin' && member.id !== user?.id ? (
                          <Select value={member.role} onValueChange={v => handleRoleChange(member.id, v)}>
                            <SelectTrigger className="w-36 h-7 text-xs border-gray-200 dark:border-gray-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="commercial">Commercial</SelectItem>
                              <SelectItem value="manager">Manager</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${ROLE_CONFIG[member.role]?.color || 'bg-gray-100 text-gray-600'}`}>
                            {ROLE_CONFIG[member.role]?.label || member.role}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Pipeline tab */}
          <TabsContent value="pipeline">
            <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
              <div className="p-5 border-b border-gray-100 dark:border-gray-800">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Étapes du pipeline</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{stages.length} étapes configurées</p>
              </div>
              <div className="p-4 space-y-2">
                {stages.map((stage, index) => (
                  <div key={stage.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30" data-testid={`stage-item-${stage.id}`}>
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-xs font-bold text-gray-400 w-4">{index + 1}</span>
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{stage.name}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Probabilité: <strong>{stage.win_probability}%</strong></span>
                    </div>
                  </div>
                ))}
                {user?.role !== 'admin' && (
                  <p className="text-xs text-gray-400 mt-2 text-center">Seuls les admins peuvent modifier les étapes du pipeline</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Offers tab */}
          <TabsContent value="offers">
            <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
              <div className="p-5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Modèles d'offres</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Templates pour pré-remplir les plans de paiement</p>
                </div>
                <Button onClick={openNewOffer} size="sm" className="h-8 bg-[#1B99E5] hover:bg-[#0f86cc] text-white text-xs" data-testid="create-offer-btn">
                  <Plus size={13} className="mr-1" /> Nouvelle offre
                </Button>
              </div>
              {offers.length === 0 ? (
                <div className="p-8 text-center">
                  <Tag size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Aucun modèle d'offre</p>
                  <p className="text-xs text-gray-400 mt-1">Créez des modèles pour accélérer la création d'échéanciers.</p>
                  <Button onClick={openNewOffer} size="sm" variant="ghost" className="mt-3 text-[#1B99E5] text-xs">
                    <Plus size={12} className="mr-1" /> Créer un modèle
                  </Button>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700">
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Nom</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Initial</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Échéances</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Récurrent</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Durée</th>
                      <th className="px-4 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {offers.map(offer => (
                      <tr key={offer.id} className="border-b border-gray-100 dark:border-gray-800 last:border-0 h-12" data-testid={`offer-row-${offer.id}`}>
                        <td className="px-4 py-2">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{offer.name}</p>
                          {offer.description && <p className="text-xs text-gray-400 truncate max-w-[180px]">{offer.description}</p>}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hidden md:table-cell">
                          {offer.initial_amount ? `${Number(offer.initial_amount).toLocaleString('fr-FR')} €` : '—'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hidden md:table-cell">{offer.initial_installments || 1}</td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hidden lg:table-cell">
                          {offer.has_recurring ? `${Number(offer.recurring_amount).toLocaleString('fr-FR')} €` : '—'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hidden lg:table-cell">
                          {offer.has_recurring ? `${offer.recurring_count} × ${FREQ_LABELS[offer.recurring_frequency] || offer.recurring_frequency}` : '—'}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1 justify-end">
                            <button onClick={() => openEditOffer(offer)} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-[#1B99E5]" data-testid={`edit-offer-${offer.id}`}>
                              <Edit2 size={13} />
                            </button>
                            <button onClick={() => handleDeleteOffer(offer.id)} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-red-500" data-testid={`delete-offer-${offer.id}`}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Backup section - admin only */}
        {user?.role === 'admin' && (
          <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5 mt-5">
            <div className="flex items-center gap-2 mb-3">
              <Database size={15} className="text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Sauvegarde des données</h3>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Exportez toutes vos données (devis, contacts, entreprises, activités) dans un fichier JSON.
              <strong className="text-[#1B99E5]"> À effectuer avant tout redéploiement</strong> pour éviter toute perte de données.
            </p>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleBackupDB}
                variant="outline"
                className="h-9 px-4 text-sm border-[#1B99E5] text-[#1B99E5] hover:bg-[#1B99E5]/10"
                data-testid="backup-db-btn"
              >
                <Database size={14} className="mr-1.5" /> Exporter le backup (JSON)
              </Button>
              <p className="text-xs text-gray-400">Contient toutes les données sauf les mots de passe</p>
            </div>
          </div>
        )}
      </div>

      {/* Create user dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="sm:max-w-sm" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Inviter un collaborateur</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Nom complet *</Label>
              <Input value={createForm.name} onChange={e => setCreateForm({ ...createForm, name: e.target.value })} placeholder="Jean Dupont" data-testid="new-user-name" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Email *</Label>
              <Input type="email" value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value })} placeholder="jean@exemple.com" data-testid="new-user-email" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Mot de passe temporaire *</Label>
              <div className="relative">
                <Input type={showPass ? 'text' : 'password'} value={createForm.password} onChange={e => setCreateForm({ ...createForm, password: e.target.value })} placeholder="Min. 6 caractères" className="pr-10" data-testid="new-user-password" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Rôle</Label>
              <Select value={createForm.role} onValueChange={v => setCreateForm({ ...createForm, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
              Le collaborateur pourra se connecter avec cet email et ce mot de passe. Invitez-le à le modifier depuis ses paramètres de profil.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)}>Annuler</Button>
            <Button onClick={handleCreateUser} disabled={creatingUser} className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white" data-testid="confirm-create-user">
              {creatingUser ? 'Création...' : 'Créer le compte'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offer create/edit dialog */}
      <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="offer-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editingOffer ? 'Modifier l\'offre' : 'Nouvelle offre'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Nom *</Label>
              <Input value={offerForm.name} onChange={e => setOfferForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex: Tranquillité" data-testid="offer-name-input" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Description (optionnel)</Label>
              <Input value={offerForm.description} onChange={e => setOfferForm(f => ({ ...f, description: e.target.value }))} placeholder="Description courte" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Montant initial par défaut (€)</Label>
                <Input type="number" step="0.01" value={offerForm.initial_amount} onChange={e => setOfferForm(f => ({ ...f, initial_amount: e.target.value }))} data-testid="offer-initial-amount" />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Nombre d'échéances</Label>
                <Select value={String(offerForm.initial_installments || 1)} onValueChange={v => setOfferForm(f => ({ ...f, initial_installments: parseInt(v) }))}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['1','2','3','4','6','12'].map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Paiement récurrent par défaut</p>
                <p className="text-xs text-gray-400">Activer si l'offre inclut un abonnement</p>
              </div>
              <button
                onClick={() => setOfferForm(f => ({ ...f, has_recurring: !f.has_recurring }))}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${offerForm.has_recurring ? 'bg-[#1B99E5]' : 'bg-gray-200 dark:bg-gray-700'}`}
                data-testid="offer-toggle-recurring"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${offerForm.has_recurring ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {offerForm.has_recurring && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-medium mb-1 block">Montant récurrent (€)</Label>
                  <Input type="number" step="0.01" value={offerForm.recurring_amount} onChange={e => setOfferForm(f => ({ ...f, recurring_amount: e.target.value }))} data-testid="offer-recurring-amount" />
                </div>
                <div>
                  <Label className="text-xs font-medium mb-1 block">Fréquence</Label>
                  <Select value={offerForm.recurring_frequency} onValueChange={v => setOfferForm(f => ({ ...f, recurring_frequency: v }))}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Mensuel</SelectItem>
                      <SelectItem value="quarterly">Trimestriel</SelectItem>
                      <SelectItem value="annual">Annuel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium mb-1 block">Nombre de périodes</Label>
                  <Input type="number" min="1" value={offerForm.recurring_count} onChange={e => setOfferForm(f => ({ ...f, recurring_count: e.target.value }))} />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOfferDialog(false)}>Annuler</Button>
            <Button onClick={handleSaveOffer} disabled={savingOffer} className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white" data-testid="save-offer-btn">
              <Save size={13} className="mr-1.5" /> {savingOffer ? 'Sauvegarde...' : editingOffer ? 'Mettre à jour' : 'Créer l\'offre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
